import { Alert, Grid, Paper, Snackbar, Stack } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import EditTools from '../components/forms/EditTools';
import TextInput from '../components/forms/TextInput';
import { useEndpoints } from '../utils/EndpointContext';
import { useParams } from 'react-router-dom';
import { FileModel } from '../models/FileModel';
import { defaultValues } from '../models/defaultValues';
import { Informative } from '../models/InformativeModel';
import { EditableContext } from '../contexts/EditableContext';

type Props = {}
type Params = {
    companyCode: string;
    productType: string;
    versionId: string;
};

const InformativesPage: React.FC = (props: Props) => {

    const { companyCode, versionId } = useParams<Params>();

    const endPoints = useEndpoints();
    const itemsEndRef = useRef<null | HTMLDivElement>(null);
    const [itemAdded, setItemAdded] = useState(false);
    const [savedData, setSavedData] = useState<FileModel>();
    const [informatives, setInformatives] = useState<Informative[]>();
    const [openAlert, setOpenAlert] = useState<boolean>(false);

    const isEditable = !savedData?.DatePublished;

    useEffect(() => {
        axios.get(`${endPoints.questionEditorRead}`,{
            params: {
                partitionKey: companyCode,
                rowKey: versionId,
                downloadData: true
            }
        })
        .then(response => {
            setSavedData(response.data);
            setInformatives(response.data.Data!.Informatives);
        });
    }, []);

    useEffect(() => {
        if (itemAdded && itemsEndRef.current) {
          itemsEndRef.current.scrollIntoView({ behavior: 'smooth' });
          setItemAdded(false);
        }
    }, [itemAdded]);

    const handleUpdateKey = (value: string, index: number) => {
        setInformatives(prevData => 
            prevData!.map((item, i) => 
                i === index ? { ...item, Id: value } : item
            )
        );
    };
    
    const handleUpdateContent = (value: string, index: number) => {
        setInformatives(prevData => 
            prevData!.map((item, i) => 
                i === index ? { ...item, Content: value } : item
            )
        );
    };

    const handleCreate = () => {
        setInformatives(prevData => [...prevData!, { ...defaultValues.informative }]);
        setItemAdded(true);
    };

    const handleDelete = (index: number) => {
        setInformatives(prevData => prevData!.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        try {
            savedData!.Data!.Informatives = informatives!;
            await axios.post(`${endPoints.questionEditorUpdate}`, savedData);
        } catch (error) {
            console.log(error);
        } finally {
            setOpenAlert(true);
        }
    };

    const handleAlertClose = () => {
        setOpenAlert(false);
    }

    return(
        <div>
            {informatives ?
            <EditableContext.Provider value={isEditable}>
                <EditTools onSave={handleSave} onCreate={handleCreate} />
                <Stack spacing={2}>
                    {informatives.map((item, index) => (
                        <Paper key={index}>
                            <Stack>
                                <Grid container spacing={2} sx={{mb: "1em"}}>
                                    <Grid item xs={3}>
                                        <TextInput label={'Key'} fieldName={'key'} value={item.Id || ''} onChange={(value) => handleUpdateKey(value, index)} />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <TextInput label={'Content'} fieldName={'Content'} value={item.Content || ''} onChange={(value) => handleUpdateContent(value, index)} />
                                    </Grid>
                                    <Grid item xs={1} className='actions-bar'>
                                        {isEditable && <Button color='error' onClick={() => handleDelete(index)}> <DeleteIcon /></Button>}
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
                <div ref={itemsEndRef}></div>
                <EditTools onSave={handleSave} onCreate={handleCreate} />
                <Snackbar open={openAlert} autoHideDuration={6000} onClose={handleAlertClose}>
                    <Alert severity="success" sx={{ width: '100%' }}>
                        Informatives updated successfully.
                    </Alert>
                </Snackbar>
            </EditableContext.Provider>
            : <>Loading Informatives</>}
        </div>
    );
}

export default InformativesPage;